/**
 * Corner handles for resizing a build plane.
 *
 * Dragging a corner moves that corner while the opposite one stays put — the
 * way a rectangle is resized in a 2D editor — rather than scaling about the
 * centre as the transform gizmo would. The handles live in world space (a
 * child of the plane's Group would inherit its non-uniform scale) and are
 * re-placed from the plane's corners whenever it changes.
 */

import { BoxGeometry, type Camera, Group, Mesh, MeshBasicMaterial, type Object3D, Vector3 } from "three";
import type { CameraRig } from "./cameraRig.ts";
import { HAKI_BLUE, SAFETY_ORANGE } from "./materials.ts";
import type { Picker } from "./picking.ts";

export type CornerEvents = Readonly<{
  /** The plane's Group changed shape. */
  change: () => void;
  dragEnd: () => void;
}>;

/** Handle size as a fraction of its distance to the camera — constant on screen. */
const SCREEN_SIZE = 0.014;
const MIN_SIZE = 0.08;

/** Sizes snap to this, metres, when snapping is on. */
const SIZE_SNAP = 0.1;

/** Smallest side the corner drag allows, metres. */
const MIN_SIDE = 0.5;

/** The four corners in the plane's local frame — a unit slab has them at ±0.5. */
const CORNERS: readonly (readonly [number, number])[] = [
  [-1, -1],
  [1, -1],
  [-1, 1],
  [1, 1],
];

const HANDLE_GEOMETRY = new BoxGeometry(1, 1, 1);

type Drag = Readonly<{
  sx: number;
  sz: number;
  /** The opposite corner, world metres — it stays put. */
  fixed: Vector3;
  y: number;
}>;

/** Yaw of a rotation about Y, radians, from the quaternion. */
function yawOf(target: Object3D): number {
  return 2 * Math.atan2(target.quaternion.y, target.quaternion.w);
}

export class PlaneCorners {
  readonly group = new Group();

  private readonly handles: Mesh[] = [];
  private readonly materials: MeshBasicMaterial[] = [];
  private readonly picker: Picker;
  private readonly rig: CameraRig;
  private readonly domElement: HTMLElement;
  private readonly events: CornerEvents;
  private target: Object3D | null = null;
  private active = false;
  private snap = true;
  private hover: number | null = null;
  private drag: Drag | null = null;
  private pressOn = false;

  constructor(picker: Picker, rig: CameraRig, domElement: HTMLElement, events: CornerEvents) {
    this.picker = picker;
    this.rig = rig;
    this.domElement = domElement;
    this.events = events;

    CORNERS.forEach(([sx, sz], index) => {
      const material = new MeshBasicMaterial({
        color: HAKI_BLUE,
        depthTest: false,
        transparent: true,
        opacity: 0.9,
      });
      const handle = new Mesh(HANDLE_GEOMETRY, material);
      handle.renderOrder = 999;
      handle.userData = { corner: index, sx, sz };
      this.handles.push(handle);
      this.materials.push(material);
      this.group.add(handle);
    });

    domElement.addEventListener("pointerdown", this.onPointerDown, true);
    domElement.addEventListener("pointermove", this.onPointerMove);
    domElement.addEventListener("pointerup", this.onPointerUp);
    this.apply();
  }

  dispose(): void {
    this.domElement.removeEventListener("pointerdown", this.onPointerDown, true);
    this.domElement.removeEventListener("pointermove", this.onPointerMove);
    this.domElement.removeEventListener("pointerup", this.onPointerUp);
    for (const material of this.materials) {
      material.dispose();
    }
  }

  /** The plane Group to resize, or null to hide the handles. */
  attach(target: Object3D | null): void {
    this.target = target;
    this.place();
    this.apply();
  }

  setActive(active: boolean): void {
    this.active = active;
    this.apply();
  }

  setSnap(snap: boolean): void {
    this.snap = snap;
  }

  private apply(): void {
    this.group.visible = this.active && this.target !== null;
    if (!this.group.visible) {
      this.hover = null;
    }
  }

  isPointerOn(): boolean {
    return this.group.visible && (this.hover !== null || this.drag !== null);
  }

  wasPressOn(): boolean {
    return this.pressOn;
  }

  /** Put each handle on its corner of the plane. */
  place(): void {
    const target = this.target;
    if (!target) {
      return;
    }

    target.updateMatrixWorld(true);
    this.handles.forEach((handle, index) => {
      const [sx, sz] = CORNERS[index] ?? [0, 0];
      handle.position.copy(target.localToWorld(new Vector3(sx * 0.5, 0, sz * 0.5)));
    });
  }

  /** Keep the handles the same size on screen. Called once per frame. */
  update(camera: Camera): void {
    if (!this.group.visible) {
      return;
    }

    for (const handle of this.handles) {
      const size = Math.max(MIN_SIZE, handle.position.distanceTo(camera.position) * SCREEN_SIZE);
      handle.scale.setScalar(size);
    }
  }

  private setHover(index: number | null): void {
    if (index === this.hover) {
      return;
    }

    this.hover = index;
    this.materials.forEach((material, i) => {
      material.color.setHex(i === index ? SAFETY_ORANGE : HAKI_BLUE);
    });
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    this.pressOn = this.group.visible && this.hover !== null && event.button === 0;
    const target = this.target;
    if (!this.pressOn || !target || this.hover === null) {
      return;
    }

    const [sx, sz] = CORNERS[this.hover] ?? [1, 1];
    target.updateMatrixWorld(true);
    this.drag = {
      sx,
      sz,
      fixed: target.localToWorld(new Vector3(-sx * 0.5, 0, -sz * 0.5)),
      y: target.position.y,
    };
    this.rig.setEnabled(false);
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.group.visible) {
      return;
    }
    if (this.drag) {
      this.resize(event);
      return;
    }

    const hit = this.picker.first(event, this.handles);
    const corner: unknown = hit?.object.userData.corner;
    this.setHover(typeof corner === "number" ? corner : null);
  };

  private readonly onPointerUp = (): void => {
    if (!this.drag) {
      return;
    }

    this.drag = null;
    this.rig.setEnabled(true);
    this.events.dragEnd();
  };

  /**
   * Move the dragged corner to the pointer. The pointer is taken on the plane's
   * own height; the vector from the fixed corner is read in the plane's yawed
   * frame, so the sides follow the plane's axes however it is turned.
   */
  private resize(event: PointerEvent): void {
    const { drag, target } = this;
    if (!drag || !target) {
      return;
    }

    const point = this.picker.onPlane(event, drag.y);
    if (!point) {
      return;
    }

    const yaw = yawOf(target);
    const cos = Math.cos(yaw);
    const sin = Math.sin(yaw);
    const vx = point.x - drag.fixed.x;
    const vz = point.z - drag.fixed.z;
    const localX = vx * cos - vz * sin;
    const localZ = vx * sin + vz * cos;

    const side = (value: number): number => {
      const snapped = this.snap ? Math.round(Math.abs(value) / SIZE_SNAP) * SIZE_SNAP : Math.abs(value);
      return Math.max(MIN_SIDE, snapped);
    };
    const w = side(localX);
    const d = side(localZ);

    // Centre = fixed corner + half the new size along the dragged corner's quadrant.
    const hx = drag.sx * (w / 2);
    const hz = drag.sz * (d / 2);
    target.position.set(drag.fixed.x + hx * cos + hz * sin, drag.y, drag.fixed.z - hx * sin + hz * cos);
    target.scale.set(w, 1, d);
    this.place();
    this.events.change();
  }
}
